import { useEffect, useMemo } from "react"
import { useDebouncedValue } from "@/hooks/useDebouncedValue.jsx"

// Committet den zum Slider nächstliegenden selektierbaren Timeline-Step (debounced,
// damit Dragging nicht pro Tick einen WMS-Request auslöst). Liefert den Controller
// für den Preset zurück: außerhalb der verfügbaren Wetter-Timesteps wird kein
// Wetter gerendert, statt stumm den nächstliegenden Layer zu zeigen — der
// User-Toggle im Panel bleibt davon unberührt.
export function useWeatherTimeBridge({ controller, selectedTimestamp }) {
  const { steps, committedIndex } = controller.time.state
  const { commitIndex } = controller.time
  const debouncedTimestamp = useDebouncedValue(selectedTimestamp)

  const targetMs = debouncedTimestamp ? Date.parse(debouncedTimestamp) : NaN

  const [firstSelectableMs, lastSelectableMs] = useMemo(() => {
    let first = null
    let last = null
    for (const step of steps) {
      if (!step.selectable) continue
      if (first == null) first = step.timestampMs
      last = step.timestampMs
    }
    return [first, last]
  }, [steps])

  // "before" | "after" | null — Richtung fürs Panel ("data starts/ends …")
  let timeOutOfRange = null
  if (Number.isFinite(targetMs) && firstSelectableMs != null) {
    if (targetMs < firstSelectableMs) timeOutOfRange = "before"
    else if (targetMs > lastSelectableMs) timeOutOfRange = "after"
  }

  useEffect(() => {
    if (!Number.isFinite(targetMs) || timeOutOfRange || steps.length === 0)
      return

    let bestIndex = -1
    let bestDiff = Infinity
    for (const step of steps) {
      if (!step.selectable) continue
      const diff = Math.abs(step.timestampMs - targetMs)
      if (diff < bestDiff) {
        bestDiff = diff
        bestIndex = step.index
      }
    }
    // Kein Re-Commit desselben Index: bei Timeline-Rebuilds stellt der Controller
    // den Stand selbst wieder her, ein zweiter Commit kostet einen WMS-Request
    if (bestIndex >= 0 && bestIndex !== committedIndex) commitIndex(bestIndex)
  }, [targetMs, timeOutOfRange, steps, committedIndex, commitIndex])

  return useMemo(
    () => ({
      presetController: timeOutOfRange
        ? { ...controller, weatherVisible: false }
        : controller,
      timeOutOfRange,
    }),
    [controller, timeOutOfRange],
  )
}
